import { Roles } from 'enums';
import jwt from 'utils/jwt';
import useUserStore from 'stores/user-store';

function normalizeRole(role: string | undefined): Roles {
  switch (role?.toLowerCase()) {
    case 'owner':
      return Roles.OWNER;
    case 'agencyadmin':
      return Roles.AGENCY;
    case 'admin':
    case 'manager':
      return Roles.MANAGER;
    case 'member':
    case 'basic':
      return Roles.BASIC;
    default:
      return Roles.UN_AUTH;
  }
}

function getTenantFromToken(accessToken: string | undefined): string {
  if (!accessToken) return '';
  try {
    const decoded = jwt.decode(accessToken, { complete: true }) as { payload?: { tenantId?: string } } | null;
    return decoded?.payload?.tenantId || '';
  } catch {
    return '';
  }
}

/**
 * Platform administration, read from the token's `platformAdmin` claim.
 *
 * Deliberately not derived from `role`: a tenant role says what someone may do
 * inside their own workspace, and every self-registered user is `admin` there.
 * Treating that as platform standing is what exposed the admin panel to new
 * accounts. The claim is issued and re-verified by the API — this is only about
 * what the UI offers; the API enforces it independently on every request.
 */
function getPlatformAdminFromToken(accessToken: string | undefined): boolean {
  if (!accessToken) return false;
  try {
    const decoded = jwt.decode(accessToken, { complete: true }) as { payload?: { platformAdmin?: boolean } } | null;
    return decoded?.payload?.platformAdmin === true;
  } catch {
    return false;
  }
}

export const useUserData = () => {
  const { basics, details, token, products } = useUserStore((state) => ({
    basics: state.basics,
    details: state.details,
    token: state.token,
    products: state.products,
  }));

  // tenant: from user details (API /users/me) or fallback to JWT payload for Copy Motive Analysis URL
  const tenant = details?.tenant || getTenantFromToken(token?.accessToken) || '';
  const tenantRole = details?.tenants?.find((t) => t.tenantId === tenant)?.role;
  const role = normalizeRole(tenantRole || details?.role || basics?.role);

  const isAuthenticated = () => {
    const validRoles = [Roles.BASIC, Roles.MANAGER, Roles.ADMIN, Roles.AGENCY, Roles.OWNER];
    return validRoles.includes(role);
  };

  const isPlatformAdmin = getPlatformAdminFromToken(token?.accessToken);

  return {
    isPlatformAdmin,
    token: token?.accessToken || '',
    firstName: details?.firstName || '',
    lastName: details?.lastName || '',
    email: details?.email || '',
    role,
    tenant,
    products: products,
    language: details?.language || 'de',
    id: details?.id || '',
    isAuthenticated,
    createdAt: details?.createdAt || '',
    onboardingDone: details?.onboardingDone || !!localStorage.getItem('onboarding-done') || false,
  };
};
